/**
 * Frontend NDJSON streaming client for Extraction.
 *
 * Uses fetch + ReadableStream to consume NDJSON from the extraction
 * Lambda Function URL.
 *
 * NDJSON event protocol:
 *   { type: "extraction:start", category: "..." }
 *   { type: "extraction:progress", category: "...", phase: "synthesizing" }
 *   { type: "extraction:item", item: {...}, index: N, id?: "uuid", outcome?: "inserted" | "matched" | "dismissed" | "awaiting" }
 *   { type: "extraction:complete", total: N, partial?: true }
 *   { type: "extraction:queued", category: "...", runId: "uuid" }
 *   { type: "extraction:error", message: "..." }
 *   { type: "heartbeat" }
 *
 * `partial: true` on `extraction:complete` means the server could not read the
 * AI's whole answer: it saved what it read and kept the existing rows it did
 * not reach, instead of removing them.
 *
 * `extraction:queued` is the OTHER terminal event: the backend handed the work
 * to an async sweep worker, so no items and no `extraction:complete` will ever
 * arrive on this stream. It is a clean end, not a disconnect — the store's
 * sweep watcher takes over from the run record.
 *
 * The server has already SAVED every item before it streams it. `id` is the row
 * the item became and `outcome` says how: a newly inserted row, an existing row
 * it matched, a dismissed tombstone it matched, or (Find new items only, mig
 * 194) a row held awaiting review — new, or one still waiting from an earlier
 * run. Both are absent when the server could not place every item (or is an
 * older build) — callers must then not act on rows by id.
 */

import { getAuthToken } from '@/lib/api'
import type {
  AnalysisCategory,
  ExtractionCategory,
  ExtractedItem,
  ExtractionPhase,
} from '@/types/extraction'

// ===================
// Types
// ===================

export type ExtractionStreamEventType =
  | 'extraction:start'
  | 'extraction:progress'
  | 'extraction:item'
  | 'extraction:complete'
  | 'extraction:queued'
  | 'extraction:error'
  | 'heartbeat'

export interface ExtractionStreamEvent {
  type: ExtractionStreamEventType
  category?: ExtractionCategory
  phase?: ExtractionPhase
  item?: ExtractedItem
  index?: number
  total?: number
  message?: string
  /** Set on `extraction:queued` — the async sweep run to watch from here on. */
  runId?: string
  /** Set on `extraction:item` — the saved row this item became. */
  id?: string
  /** Set on `extraction:item` alongside `id`. */
  outcome?: string
  /** Set (true) on `extraction:complete` when the run read only part of the answer. */
  partial?: boolean
}

/**
 * How a streamed item landed in the database. `awaiting` is a row held for the
 * user's review (Find new items, mig 194) — not in the table yet.
 */
export type ExtractionItemOutcome = 'inserted' | 'matched' | 'dismissed' | 'awaiting'

/**
 * Where a streamed item was saved. Empty when the server sent no row id — an
 * older backend, or a run whose items could not all be placed.
 */
export interface ExtractionItemMeta {
  id?: string
  outcome?: ExtractionItemOutcome
}

const ITEM_OUTCOMES: readonly ExtractionItemOutcome[] = ['inserted', 'matched', 'dismissed', 'awaiting']

function isItemOutcome(value: unknown): value is ExtractionItemOutcome {
  return typeof value === 'string' && (ITEM_OUTCOMES as readonly string[]).includes(value)
}

export interface StreamExtractionParams {
  bidId: string
  category: AnalysisCategory
  /**
   * `find_new` = Find new items: the server saves new rows awaiting review
   * instead of straight into the table. Absent = an ordinary extraction, whose
   * rows are saved active.
   */
  mode?: 'find_new'
}

export interface StreamExtractionCallbacks {
  onStart?: (category: ExtractionCategory) => void
  onProgress?: (phase: ExtractionPhase) => void
  onItem: (item: ExtractedItem, index: number, meta: ExtractionItemMeta) => void
  /** `info` is passed only for a partial read; a whole read calls `onComplete(total)`. */
  onComplete: (total: number, info?: { partial: true }) => void
  /**
   * The run was handed to an async sweep worker. Terminal: this stream is done
   * and `onComplete` will never fire. The caller owns watching the run record.
   */
  onQueued?: (runId: string) => void
  onError?: (message: string) => void
}

// ===================
// Configuration
// ===================

const EXTRACTION_STREAM_URL = (import.meta.env['VITE_EXTRACTION_STREAM_URL'] ?? '').trim()
const STREAM_IDLE_TIMEOUT_MS = 600_000 // 10 min — large tenders with 10+ docs can take 5min+ for LLM synthesis

/**
 * Check if extraction streaming is available (URL is configured).
 */
export function isExtractionStreamingAvailable(): boolean {
  return EXTRACTION_STREAM_URL.length > 0
}

// ===================
// Streaming Client
// ===================

/**
 * Start extraction and stream results via NDJSON.
 *
 * Uses fetch + ReadableStream + TextDecoderStream for real-time parsing.
 * Caller should check isExtractionStreamingAvailable() before calling.
 */
export async function streamExtraction(
  params: StreamExtractionParams,
  callbacks: StreamExtractionCallbacks,
  signal?: AbortSignal
): Promise<void> {
  if (!isExtractionStreamingAvailable()) {
    throw new ExtractionStreamError(
      'CONFIG_ERROR',
      'Extraction streaming is not configured. Set VITE_EXTRACTION_STREAM_URL and restart the frontend.'
    )
  }

  const token = await getAuthToken()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const response = await fetch(EXTRACTION_STREAM_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
    ...(signal ? { signal } : {}),
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new ExtractionStreamError(
      'STREAM_ERROR',
      `Extraction stream request failed: ${response.status} ${errorText}`
    )
  }

  if (!response.body) {
    throw new ExtractionStreamError('NO_BODY', 'Response body is null')
  }

  // Parse NDJSON stream
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  let streamIdleTimedOut = false
  let receivedComplete = false
  let receivedQueued = false
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      streamIdleTimedOut = true
      void reader.cancel('stream_idle_timeout')
    }, STREAM_IDLE_TIMEOUT_MS)
  }
  resetIdleTimer()

  try {
    for (;;) {
      const { done, value } = await reader.read()

      if (done) break
      resetIdleTimer()

      buffer += value
      const lines = buffer.split('\n')
      // Keep the last (potentially incomplete) line in the buffer
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed.length === 0) continue

        try {
          const event = JSON.parse(trimmed) as ExtractionStreamEvent
          if (event.type === 'extraction:complete') receivedComplete = true
          if (event.type === 'extraction:queued') receivedQueued = true
          handleEvent(event, callbacks)
        } catch {
          // Skip malformed lines — may be partial chunk boundaries
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim().length > 0) {
      try {
        const event = JSON.parse(buffer.trim()) as ExtractionStreamEvent
        if (event.type === 'extraction:complete') receivedComplete = true
        if (event.type === 'extraction:queued') receivedQueued = true
        handleEvent(event, callbacks)
      } catch {
        // Incomplete final line — discard
      }
    }

    if (streamIdleTimedOut) {
      throw new ExtractionStreamError(
        'STREAM_IDLE_TIMEOUT',
        'Extraction timed out. Please try again.'
      )
    }

    // Stream ended without either terminal event — server likely timed out or
    // crashed. `extraction:queued` is the async-sweep terminator: the work
    // moved to a worker, so ending here is by design, not a disconnect.
    if (!receivedComplete && !receivedQueued) {
      throw new ExtractionStreamError(
        'STREAM_DISCONNECTED',
        'Extraction was interrupted. The server may have timed out. Please try again.'
      )
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer)
    reader.releaseLock()
  }
}

// ===================
// Event Handling
// ===================

function handleEvent(event: ExtractionStreamEvent, callbacks: StreamExtractionCallbacks): void {
  switch (event.type) {
    case 'extraction:start':
      if (event.category && callbacks.onStart) {
        callbacks.onStart(event.category)
      }
      break

    case 'extraction:progress':
      if (event.phase && callbacks.onProgress) {
        callbacks.onProgress(event.phase)
      }
      break

    case 'extraction:item':
      if (event.item != null && event.index != null) {
        // An id without a known outcome (or the reverse) is not trustworthy
        // enough to act on by id, so both are passed or neither is.
        const meta: ExtractionItemMeta =
          typeof event.id === 'string' && event.id !== '' && isItemOutcome(event.outcome)
            ? { id: event.id, outcome: event.outcome }
            : {}
        callbacks.onItem(event.item, event.index, meta)
      }
      break

    case 'extraction:complete':
      if (event.total != null) {
        if (event.partial === true) {
          callbacks.onComplete(event.total, { partial: true })
        } else {
          callbacks.onComplete(event.total)
        }
      }
      break

    case 'extraction:queued':
      if (event.runId && callbacks.onQueued) {
        callbacks.onQueued(event.runId)
      }
      break

    case 'extraction:error':
      if (callbacks.onError && event.message) {
        callbacks.onError(event.message)
      }
      break

    case 'heartbeat':
      // Server keepalive — no action needed
      break
  }
}

// ===================
// Error Class
// ===================

export class ExtractionStreamError extends Error {
  code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'ExtractionStreamError'
    this.code = code
  }
}
